/**
 * Pattern Library for Ultimate AI
 * Recognizes common board patterns and suggests good moves
 */

import { GameState, PieceType, Position, IMove } from "../../types";

export interface Pattern {
  name: string;
  priority: number;
  matches: (gameState: GameState) => boolean;
  suggestMove?: (gameState: GameState) => { fromPosition: Position; move: IMove } | null;
}

export class PatternLibrary {
  private patterns: Pattern[] = [];

  constructor() {
    this.initializePatterns();
  }

  /**
   * Check if any patterns match the current position
   */
  matchPatterns(gameState: GameState): Pattern[] {
    return this.patterns
      .filter((pattern) => pattern.matches(gameState))
      .sort((a, b) => b.priority - a.priority);
  }

  /**
   * Get suggested move from highest priority matching pattern
   */
  getSuggestedMove(gameState: GameState): { fromPosition: Position; move: IMove } | null {
    const matches = this.matchPatterns(gameState);

    for (const pattern of matches) {
      if (pattern.suggestMove) {
        const move = pattern.suggestMove(gameState);
        if (move) return move;
      }
    }

    return null;
  }

  /**
   * Initialize pattern recognition rules
   */
  private initializePatterns(): void {
    // Opening patterns
    this.patterns.push({
      name: "Early Game King Advancement",
      priority: 5,
      matches: (gameState) => gameState.turnCount <= 3,
      suggestMove: (gameState) => this.suggestKingAdvancement(gameState),
    });

    // Tactical patterns
    this.patterns.push({
      name: "King Near Goal",
      priority: 10,
      matches: (gameState) => this.hasKingNearGoal(gameState),
      suggestMove: (gameState) => this.suggestRaceMove(gameState),
    });

    this.patterns.push({
      name: "Free Capture Available",
      priority: 8,
      matches: (gameState) => this.hasFreeCapture(gameState),
      suggestMove: (gameState) => this.suggestFreeCapture(gameState),
    });

    // Defensive patterns
    this.patterns.push({
      name: "King in Danger",
      priority: 9,
      matches: (gameState) => this.isKingInDanger(gameState),
      suggestMove: (gameState) => this.suggestKingEscape(gameState),
    });

    // Positional patterns
    this.patterns.push({
      name: "Center Control",
      priority: 3,
      matches: (gameState) => gameState.turnCount <= 10,
      suggestMove: (gameState) => this.suggestCenterControl(gameState),
    });
  }

  /**
   * Check if king is near goal
   */
  private hasKingNearGoal(gameState: GameState): boolean {
    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 5; col++) {
        const pieces = gameState.board[row][col].Pieces;
        for (const piece of pieces) {
          if (piece.poisonedStatus?.isPoisoned) continue;
          if (!piece.isKing) continue;
          // Allow control if piece is the current player's color, or if it is zombified
          const isOwnColor = piece.isLight === gameState.isLightTurn;
          const isZombified = piece.zombifiedStatus?.isZombified || false;
          if (!isOwnColor && !isZombified) continue;

          const goalRow = piece.isLight ? 7 : 0;
          const distance = Math.abs(row - goalRow);

          if (distance <= 2) return true;
        }
      }
    }
    return false;
  }

  /**
   * Suggest race move (king advancement)
   */
  private suggestRaceMove(gameState: GameState): { fromPosition: Position; move: IMove } | null {
    // Find king
    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 5; col++) {
        const pieces = gameState.board[row][col].Pieces;
        for (const piece of pieces) {
          if (piece.poisonedStatus?.isPoisoned) continue;
          if (!piece.isKing) continue;
          // Allow control if piece is the current player's color, or if it is zombified
          const isOwnColor = piece.isLight === gameState.isLightTurn;
          const isZombified = piece.zombifiedStatus?.isZombified || false;
          if (!isOwnColor && !isZombified) continue;

          const position: Position = { row, col };
          const moves = piece.getValidMovesForPiece(gameState.board, position, gameState.isLightTurn, undefined);

          // Find move that advances king most
          const goalRow = piece.isLight ? 7 : 0;
          let bestMove: IMove | null = null;
          let bestAdvancement = -1;

          for (const move of moves) {
            const newDistance = Math.abs(move.position.row - goalRow);
            const oldDistance = Math.abs(row - goalRow);
            const advancement = oldDistance - newDistance;

            if (advancement > bestAdvancement) {
              bestAdvancement = advancement;
              bestMove = move;
            }
          }

          if (bestMove) {
            return { fromPosition: position, move: bestMove };
          }
        }
      }
    }
    return null;
  }

  /**
   * Check if free capture is available
   */
  private hasFreeCapture(gameState: GameState): boolean {
    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 5; col++) {
        const pieces = gameState.board[row][col].Pieces;
        for (const piece of pieces) {
          if (piece.poisonedStatus?.isPoisoned) continue;
          // Allow control if piece is the current player's color, or if it is zombified
          const isOwnColor = piece.isLight === gameState.isLightTurn;
          const isZombified = piece.zombifiedStatus?.isZombified || false;
          if (!isOwnColor && !isZombified) continue;

          const position: Position = { row, col };
          const moves = piece.getValidMovesForPiece(gameState.board, position, gameState.isLightTurn, undefined);

          for (const move of moves) {
            if (move.toPiece && !this.isDefended(gameState, move.position)) {
              return true;
            }
          }
        }
      }
    }
    return false;
  }

  /**
   * Suggest free capture
   */
  private suggestFreeCapture(gameState: GameState): { fromPosition: Position; move: IMove } | null {
    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 5; col++) {
        const pieces = gameState.board[row][col].Pieces;
        for (const piece of pieces) {
          if (piece.poisonedStatus?.isPoisoned) continue;
          // Allow control if piece is the current player's color, or if it is zombified
          const isOwnColor = piece.isLight === gameState.isLightTurn;
          const isZombified = piece.zombifiedStatus?.isZombified || false;
          if (!isOwnColor && !isZombified) continue;

          const position: Position = { row, col };
          const moves = piece.getValidMovesForPiece(gameState.board, position, gameState.isLightTurn, undefined);

          for (const move of moves) {
            if (move.toPiece && !this.isDefended(gameState, move.position)) {
              return { fromPosition: position, move };
            }
          }
        }
      }
    }
    return null;
  }

  /**
   * Check if king is in danger
   */
  private isKingInDanger(gameState: GameState): boolean {
    // Find our king
    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 5; col++) {
        const pieces = gameState.board[row][col].Pieces;
        for (const piece of pieces) {
          if (piece.poisonedStatus?.isPoisoned) continue;
          if (!piece.isKing) continue;
          // Allow control if piece is the current player's color, or if it is zombified
          const isOwnColor = piece.isLight === gameState.isLightTurn;
          const isZombified = piece.zombifiedStatus?.isZombified || false;
          if (!isOwnColor && !isZombified) continue;

          const position: Position = { row, col };
          return this.isThreatened(gameState, position, piece.isLight);
        }
      }
    }
    return false;
  }

  /**
   * Suggest king escape move
   */
  private suggestKingEscape(gameState: GameState): { fromPosition: Position; move: IMove } | null {
    // Find king and move it to safety
    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 5; col++) {
        const pieces = gameState.board[row][col].Pieces;
        for (const piece of pieces) {
          if (piece.poisonedStatus?.isPoisoned) continue;
          if (!piece.isKing) continue;
          // Allow control if piece is the current player's color, or if it is zombified
          const isOwnColor = piece.isLight === gameState.isLightTurn;
          const isZombified = piece.zombifiedStatus?.isZombified || false;
          if (!isOwnColor && !isZombified) continue;

          const position: Position = { row, col };
          const moves = piece.getValidMovesForPiece(gameState.board, position, gameState.isLightTurn, undefined);

          // Find safest move
          for (const move of moves) {
            if (!this.isThreatened(gameState, move.position, piece.isLight)) {
              return { fromPosition: position, move };
            }
          }
        }
      }
    }
    return null;
  }

  /**
   * Suggest king advancement in opening
   */
  private suggestKingAdvancement(gameState: GameState): { fromPosition: Position; move: IMove } | null {
    return this.suggestRaceMove(gameState);
  }

  /**
   * Suggest center control move
   */
  private suggestCenterControl(gameState: GameState): { fromPosition: Position; move: IMove } | null {
    // Move pieces toward center columns
    const centerCols = [1, 2, 3];

    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 5; col++) {
        const pieces = gameState.board[row][col].Pieces;
        for (const piece of pieces) {
          if (piece.poisonedStatus?.isPoisoned) continue;
          // Allow control if piece is the current player's color, or if it is zombified
          const isOwnColor = piece.isLight === gameState.isLightTurn;
          const isZombified = piece.zombifiedStatus?.isZombified || false;
          if (!isOwnColor && !isZombified) continue;

          const position: Position = { row, col };
          const moves = piece.getValidMovesForPiece(gameState.board, position, gameState.isLightTurn, undefined);

          for (const move of moves) {
            if (centerCols.includes(move.position.col) && !centerCols.includes(col)) {
              return { fromPosition: position, move };
            }
          }
        }
      }
    }
    return null;
  }

  /**
   * Check if a position is defended by enemy pieces
   */
  private isDefended(gameState: GameState, position: Position): boolean {
    return this.isThreatened(gameState, position, gameState.isLightTurn);
  }

  /**
   * Check if a position is threatened by enemy pieces
   */
  private isThreatened(gameState: GameState, position: Position, isLight: boolean): boolean {
    // Check all enemy pieces to see if they can attack this position
    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 5; col++) {
        const pieces = gameState.board[row][col].Pieces;
        for (const piece of pieces) {
          if (piece.poisonedStatus?.isPoisoned) continue;
          if (piece.isLight === isLight) continue; // Same side

          const piecePosition: Position = { row, col };
          try {
            const moves = piece.getValidMovesForPiece(gameState.board, piecePosition, gameState.isLightTurn, undefined);
            for (const move of moves) {
              if (move.position.row === position.row && move.position.col === position.col) {
                return true;
              }
            }
          } catch (error) {
            // Ignore errors
          }
        }
      }
    }
    return false;
  }
}